import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Avatar, IconButton,
  Paper, Grid, Stack, Divider, List, ListItem,
  ListItemIcon, ListItemText, Button, Chip,
  ListItemButton, // 👇 इसे नया इम्पोर्ट किया गया है
  Skeleton
} from '@mui/material';
import {
  Edit, AccountBalanceWallet, CardGiftcard, ShoppingBag,
  AddCircle, Timeline, ShoppingCart, ReceiptLong,
  NotificationsActive, SmartToy, HelpOutline, Info,
  GppGood, ExitToApp, ChevronRight
} from '@mui/icons-material';
import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWallet } from '../contexts/WalletContext';

// --- Premium Design Colors ---
const themeColors = {
  bgLight: '#F1F5F9',
  primaryDark: '#1E1B4B',
  indigoGradient: 'linear-gradient(135deg, #4F46E5 0%, #312E81 100%)',
  emeraldGradient: 'linear-gradient(135deg, #10B981 0%, #047857 100%)',
  cardBg: '#FFFFFF',
  textMain: '#0F172A',
  textMuted: '#64748B',
  danger: '#EF4444',
  warning: '#F59E0B'
};

// --- Dummy Data ---
const dummyAccount = {
  name: 'Sachin',
  mobile: '9876543210',
  country_code: '91',
  email: 'sachin@example.com',
  district: 'Lucknow',
  state: 'Uttar Pradesh',
  photo: ''
};

const dummyWallet = {
  balance: 1250,
  daily_free_credit: 50,
  purchased_credit: 1200
};

const dummyPlans = [
  { id: 1, name: 'सिल्वर / Silver', credits: 100, sale_price: 99, original_price: 149, popular: false },
  { id: 2, name: 'गोल्ड / Gold', credits: 500, sale_price: 399, original_price: 599, popular: true },
  { id: 3, name: 'प्लैटिनम / Platinum', credits: 1000, sale_price: 699, original_price: 999, popular: false },
];

export default function ProfilePage() {
  const navigate = useNavigate();

  // Quick Actions Menu Configuration
  const quickActions = [
    { title: 'क्रेडिट उपयोग', subtitle: 'Credit Usage History', icon: <Timeline />, color: '#3B82F6', link: '/credit-transactions' },
    { title: 'अधिक क्रेडिट खरीदें', subtitle: 'Buy More Credits', icon: <ShoppingCart />, color: '#8B5CF6', link: '/plans' },
    { title: 'खरीदारी इतिहास', subtitle: 'Purchase History', icon: <ReceiptLong />, color: '#F59E0B', link: '/credit-purchases' },
    { title: 'सूचनाएं', subtitle: 'Notifications', icon: <NotificationsActive />, color: '#EF4444', link: '/notifications' },
    { title: 'ऑटोमेशन', subtitle: 'Automations', icon: <SmartToy />, color: '#10B981', link: '/my-automations' },
  ];

  // Support Menu Configuration
  const supportActions = [
    { title: 'सहायता और समर्थन', subtitle: 'Help & Support', icon: <HelpOutline />, color: '#06B6D4', link: 'tel:8765140002' },
    { title: 'हमारे बारे में', subtitle: 'About Us', icon: <Info />, color: '#6366F1', link: '/about' },
    { title: 'गोपनीयता नीति', subtitle: 'Privacy Policy', icon: <GppGood />, color: '#64748B', link: '/privacy' },
  ];

  const [profileData, setProfileData] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { getProfile } = useAuth();

  const { wallet, fetchWalletData, loading: walletLoading } = useWallet();

  const fetchProfileInfo = async () => {
    console.log("FETCH RUNNING AGAIN!");
    try {
      setLoading(true);
      const response = await getProfile();
      console.log("Full API Response:", response);

      if (response && response.success && response.data) {
        setProfileData(response.data);
      } else if (response && response.cust_id) {
        setProfileData(response);
      }
      await fetchWalletData();
    } catch (err) {
      console.error("Fetch Error:", err);
      setError("डेटा लोड करने में विफल।");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfileInfo();
  }, []);

  // 👇 नेविगेशन हैंडलर 
  const handleNavigation = (link) => {
    if (link.startsWith('tel:') || link.startsWith('mailto:')) {
      window.location.href = link;
    } else {
      navigate(link);
    }
  };


  return (
    <Box sx={{ bgcolor: themeColors.bgLight, minHeight: '100vh', pb: 10 }}>

      {/* 1. Profile Header & Banner */}
      <Box sx={{
        background: themeColors.indigoGradient,
        pt: { xs: 4, md: 6 }, pb: 8, px: 2,
        borderRadius: { xs: '0 0 30px 30px', md: '0 0 40px 40px' },
        boxShadow: '0 10px 30px rgba(49, 46, 129, 0.2)'
      }}>
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h5" fontWeight="800" color="white">मेरा खाता</Typography>
            <IconButton
              onClick={() => navigate('/complete-profile')}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
            >
              <Edit />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            {loading ? (
              <Skeleton variant="circular" width={80} height={80} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
            ) : (
              <Avatar src={profileData?.photo} sx={{ width: 80, height: 80, border: '3px solid white' }} />
            )}
            <Box>
              {loading ? (
                <>
                  <Skeleton width={150} height={30} sx={{ bgcolor: 'rgba(255,255,255:0.1)' }} />
                  <Skeleton width={100} sx={{ bgcolor: 'rgba(255,255,255:0.1)' }} />
                </>
              ) : (
                <>
                  {/* Name/Mobile Display */}
                  <Typography variant="h5" fontWeight="700" color="white" sx={{ lineHeight: 1.2 }}>

                    {profileData?.name ? String(profileData.name) : dummyAccount.name}
                  </Typography>

                  <Typography variant="body2" sx={{ color: 'white', opacity: 0.9 }}>

                    {profileData?.mobile
                      ? `+${profileData?.country_code || '91'} ${profileData.mobile}`
                      : `+${dummyAccount.country_code} ${dummyAccount.mobile}`}
                  </Typography>


                  {profileData?.email && (
                    <Typography variant="caption" sx={{ color: 'white', opacity: 0.7 }}>
                      {profileData.email}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="sm" sx={{ mt: -5, position: 'relative', zIndex: 2 }}>

        {/* 2. Wallet Card */}
        <Paper elevation={4} sx={{ background: themeColors.emeraldGradient, borderRadius: 4, p: 3, mb: 4, color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>
                वॉलेट बैलेंस / Wallet Balance
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <AccountBalanceWallet sx={{ fontSize: 32, opacity: 0.9 }} />
                <Typography variant="h3" fontWeight="900">
                  {walletLoading ? <Skeleton width={60} /> : (wallet?.total_available ?? 0)}
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9, pt: 1 }}>क्रेडिट</Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', my: 2 }} />

          <Grid container spacing={2} textAlign="center" mb={2}>
            <Grid item xs={6}>
              <CardGiftcard sx={{ fontSize: 20, opacity: 0.8, mb: 0.5 }} />
              <Typography variant="h6" fontWeight="700">
                {walletLoading ? '...' : (wallet?.daily_free_credit ?? 0)}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Daily Free</Typography>
            </Grid>
            <Grid item xs={6} sx={{ borderLeft: '1px solid rgba(255,255,255,0.2)' }}>
              <ShoppingBag sx={{ fontSize: 20, opacity: 0.8, mb: 0.5 }} />
              <Typography variant="h6" fontWeight="700">
                {walletLoading ? '...' : (wallet?.purchased_balance ?? 0)}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Purchased</Typography>
            </Grid>
          </Grid>

          <Button
            fullWidth variant="contained" startIcon={<AddCircle />}
            onClick={() => navigate('/plans')}
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', backdropFilter: 'blur(10px)', borderRadius: 2, py: 1.5, fontWeight: 'bold' }}
          >
            क्रेडिट खरीदें / Buy Credits
          </Button>
        </Paper>

        {/* 3. Credit Plans Section */}
        <Box id="credit-plans" sx={{ mb: 4, pt: 2 }}>
          <Typography variant="subtitle1" fontWeight="800" color={themeColors.textMain} gutterBottom sx={{ px: 1 }}>
            क्रेडिट योजनाएं / Credit Plans
          </Typography>

          <Grid container spacing={2}>
            {dummyPlans.map((plan) => (
              <Grid item xs={12} sm={6} md={4} key={plan.id}>
                <Paper
                  elevation={plan.popular ? 4 : 1}
                  sx={{
                    p: 2.5, borderRadius: 3, textAlign: 'center', position: 'relative',
                    border: plan.popular ? `2px solid ${themeColors.warning}` : '1px solid #E2E8F0',
                    transition: 'all 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }
                  }}
                >
                  {plan.popular && (
                    <Chip
                      label="POPULAR"
                      size="small"
                      sx={{ position: 'absolute', top: -10, right: 20, bgcolor: themeColors.warning, color: 'white', fontWeight: 'bold' }}
                    />
                  )}

                  <Typography variant="subtitle2" color="text.secondary" fontWeight="700" gutterBottom>
                    {plan.name}
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, my: 1.5 }}>
                    <AccountBalanceWallet sx={{ color: themeColors.warning, fontSize: 28 }} />
                    <Typography variant="h4" fontWeight="900" color={themeColors.textMain}>
                      {plan.credits}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h5" component="span" fontWeight="800" color={themeColors.textMain}>
                      ₹{plan.sale_price}
                    </Typography>
                    <Typography variant="body2" component="span" sx={{ textDecoration: 'line-through', color: 'text.disabled', ml: 1 }}>
                      ₹{plan.original_price}
                    </Typography>
                  </Box>

                  <Button
                    variant={plan.popular ? "contained" : "outlined"}
                    fullWidth
                    onClick={() => navigate('/plans')}
                    sx={{
                      borderRadius: 20, fontWeight: 'bold',
                      ...(plan.popular && { bgcolor: themeColors.warning, '&:hover': { bgcolor: '#D97706' } }),
                      ...(!plan.popular && { color: themeColors.textMain, borderColor: '#CBD5E1' })
                    }}
                  >
                    अभी खरीदें
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* 4. Quick Actions Menu */}
        <Typography variant="subtitle1" fontWeight="800" color={themeColors.textMain} gutterBottom sx={{ px: 1, mt: 4 }}>
          त्वरित कार्य / Quick Actions
        </Typography>
        <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #E2E8F0', mb: 4 }}>
          <List disablePadding>
            {quickActions.map((action, index) => (
              <React.Fragment key={index}>
                {/* 👇 FIX: ListItemButton का इस्तेमाल किया गया है */}
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation(action.link)}
                    sx={{ py: 2, '&:hover': { bgcolor: '#F8FAFC' } }}
                  >
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: `${action.color}15`, color: action.color, borderRadius: 2 }}>
                        {action.icon}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="subtitle2" fontWeight="700" color={themeColors.textMain}>{action.title}</Typography>}
                      secondary={<Typography variant="caption" color="text.secondary">{action.subtitle}</Typography>}
                    />
                    <ChevronRight sx={{ color: 'text.disabled' }} />
                  </ListItemButton>
                </ListItem>
                {index !== quickActions.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>

        {/* 5. Support Section */}
        <Typography variant="subtitle1" fontWeight="800" color={themeColors.textMain} gutterBottom sx={{ px: 1 }}>
          सहायता / Support
        </Typography>
        <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #E2E8F0', mb: 4 }}>
          <List disablePadding>
            {supportActions.map((action, index) => (
              <React.Fragment key={index}>
                {/* 👇 FIX: यहाँ भी ListItemButton का इस्तेमाल किया गया है */}
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation(action.link)}
                    sx={{ py: 2, '&:hover': { bgcolor: '#F8FAFC' } }}
                  >
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: `${action.color}15`, color: action.color, borderRadius: 2 }}>
                        {action.icon}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="subtitle2" fontWeight="700" color={themeColors.textMain}>{action.title}</Typography>}
                      secondary={<Typography variant="caption" color="text.secondary">{action.subtitle}</Typography>}
                    />
                    <ChevronRight sx={{ color: 'text.disabled' }} />
                  </ListItemButton>
                </ListItem>
                {index !== supportActions.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>

        {/* 6. Logout Button */}
        <Button
          fullWidth
          variant="outlined"
          color="error"
          startIcon={<ExitToApp />}
          onClick={() => {
            alert("Logging out...");
            navigate('/login');
          }}
          sx={{
            py: 1.5, borderRadius: 3, fontWeight: 'bold',
            borderWidth: 2, '&:hover': { borderWidth: 2, bgcolor: '#FEF2F2' },
            mb: 4
          }}
        >
          लॉग आउट / Log Out
        </Button>

      </Container>
    </Box>
  );
}